import { useCallback, useEffect } from 'react'
import { Modal as BootstrapModal } from 'react-bootstrap'

import { Modal } from '../Modal/Modal'
import { Button } from '../Button/Button'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  resetFileBrowser,
  selectFileBrowserSelectedItems,
  selectFileBrowserBreadcrumbs,
  selectFileBrowserIsAtRoot,
} from '../../store/documentFileBrowser/documentFileBrowserSlice'
import { type IntegrationServiceId, getIntegrationDisplayName } from '../../common/integrations'
import { type FileBrowserItem, type FileBrowserSourceFolder, type SelectionMode, ROOT_ANCHOR_ID } from './types'
import { IntegrationFileBrowser } from './IntegrationFileBrowser'
import styles from './DocumentFileBrowserModal.module.scss'

const getConfirmButtonText = (selectionMode: SelectionMode, count: number) => {
  switch (selectionMode) {
    case 'single-file':
      return 'Select File'
    case 'multi-file':
      return 'Add files'
    case 'single-folder':
      return 'Select Folder'
    case 'multi-folder':
      return 'Select Folders'
    case 'any':
      return count > 0 ? `Select ${count} item${count !== 1 ? 's' : ''}` : 'Select Items'
  }
}

interface DocumentFileBrowserModalProps {
  show: boolean
  onHide: () => void
  serviceId: IntegrationServiceId
  selectionMode?: SelectionMode
  onFilesSelected: (files: FileBrowserItem[], sourceFolder: FileBrowserSourceFolder | null) => void
  initialFolderIds?: string[]
  initialFoldersReady?: boolean
}

export const DocumentFileBrowserModal = ({
  show,
  onHide,
  serviceId,
  selectionMode = 'multi-file',
  onFilesSelected,
  initialFolderIds,
  initialFoldersReady,
}: DocumentFileBrowserModalProps) => {
  const dispatch = useAppDispatch()
  const selectedItems = useAppSelector(selectFileBrowserSelectedItems)
  const breadcrumbs = useAppSelector(selectFileBrowserBreadcrumbs)
  const isAtRoot = useAppSelector(selectFileBrowserIsAtRoot)

  const integrationDisplayName = getIntegrationDisplayName(serviceId)

  const isSingleFolder = selectionMode === 'single-folder'
  const currentCrumb = breadcrumbs[breadcrumbs.length - 1]
  const folderSelection: FileBrowserItem[] =
    isSingleFolder && !isAtRoot && currentCrumb ? [{ id: currentCrumb.id, name: currentCrumb.name, type: 'folder', integration: currentCrumb.integration }] : []
  const effectiveItems = isSingleFolder ? folderSelection : selectedItems

  useEffect(() => {
    return () => {
      dispatch(resetFileBrowser())
    }
  }, [dispatch])

  const buttonLabel = getConfirmButtonText(selectionMode, effectiveItems.length)

  const handleConfirm = useCallback(() => {
    if (effectiveItems.length === 0) {
      return
    }
    const sourceFolder: FileBrowserSourceFolder | null =
      !isAtRoot && currentCrumb
        ? {
            id: currentCrumb.id,
            name: currentCrumb.name,
            path: breadcrumbs
              .filter((crumb) => crumb.id !== ROOT_ANCHOR_ID)
              .map((crumb) => crumb.name)
              .join('/'),
            integration: currentCrumb.integration,
          }
        : null
    onFilesSelected(effectiveItems, sourceFolder)
    onHide()
  }, [onFilesSelected, onHide, effectiveItems, isAtRoot, currentCrumb, breadcrumbs])

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName={styles.documentFileBrowserModal}
      bodyClassName={styles.documentFileBrowserBody}
      header={<BootstrapModal.Title>Browse {integrationDisplayName} Documents</BootstrapModal.Title>}
      body={
        <IntegrationFileBrowser
          serviceId={serviceId}
          selectionMode={selectionMode}
          initialFolderIds={initialFolderIds}
          initialFoldersReady={initialFoldersReady}
        />
      }
      footer={
        <div className='d-flex zocks-gap-0.75'>
          <Button variant='secondaryGhostButton' onClick={onHide}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleConfirm} disabled={effectiveItems.length === 0}>
            {buttonLabel}
          </Button>
        </div>
      }
    />
  )
}
